/**
 * 云服务类别 产品图表页 图表Item
 *
 * @format
 */

import React, { FC, useEffect, useState, useRef, useContext } from 'react'
import { _$, Icon, Select, Option } from '@king-design/react'
import dayjs from 'dayjs'
import NoDataComponent from 'src/components/NoData'
import { CountContext } from 'src/pages/MonitorCloud/MonitorCloudChart'
import { ebs_childtypes } from 'src/containers/MonitorCloud/MonitorCloudChart/MonitorChartList'
import MonitorChartItemApp, { MonitorChartItemAppHandle } from './MonitorChartItemApp'
import styled from 'styled-components'
import { request } from 'src/utils'
import {
  calcDateFormat,
  calcSelectInterval,
  IChartDataProps,
  getQueryDataParams,
  AggregatorEnum,
  AggregatorKeys
} from '../constants'
import API from 'src/api/ApiCloud'
import { useWindowSize, useDebounce } from 'react-use'

interface MonitorChartItemProps {
  productType: number | string
  graph: any
  instanceId: string
  rangeDate: [number, number]
  isRequest: boolean
  width?: number
  height?: number
  openDetails: (value) => void
  className?: string
}

const MonitorChartItem: FC<MonitorChartItemProps> = ({
  productType,
  graph,
  instanceId,
  rangeDate,
  isRequest,
  width,
  height,
  openDetails,
  className
}) => {
  const { regionName, nameSpace } = useContext(CountContext)
  const chartRef = useRef<MonitorChartItemAppHandle>()
  // const [initDate, setInitDate] = useState<[number, number]>([undefined, undefined])
  const [chartData, setChartData] = useState<IChartDataProps>()
  const [interval, setInterval] = useState<string>('30')
  const [aggregate, setAggregate] = useState<AggregatorKeys>('max')
  const observer = new IntersectionObserver(entries => {
    if (entries[0].intersectionRatio > 0 || isRequest) {
      const container = entries[0].target
      const delay = graph.lines[0]?.delay
      const interval = calcSelectInterval(rangeDate, delay)[0]
      setInterval(interval)
      getChartList()
      observer.unobserve(container)
    }
  })

  useEffect(() => {
    if (isRequest) {
      const [start, end] = rangeDate
      if (start && end) {
        observer.observe(document.getElementById(`chart-item-${graph.graphId}`))
      }
    }
    return () => {
      observer.disconnect()
    }
  }, [graph.lines, isRequest, rangeDate])

  const getChartList = async (data?: any) => {
    const params = getQueryDataParams(graph, instanceId, rangeDate, productType)
    const result = await request(API.getMetricsData, {
      method: 'POST',
      data: {
        ...params,
        Namespace: ebs_childtypes.includes(productType) ? 'DBSC/EBS' : nameSpace,
        Aggregate: aggregate,
        ...data
      },
      region: regionName
    })
    if (result?.data?.data?.length) {
      const xData = result?.data?.data?.[0]?.datapoints?.map(({ timestamp }) => {
        return dayjs(timestamp * 1000).format(calcDateFormat(rangeDate, params.Period))
      })
      const series = []
      let maxValue = 0
      let minValue = 0
      result?.data?.data?.forEach(item => {
        const { datapoints, metricDesc } = item
        const symbol = datapoints.find(({ value }) => value === null)
        const seriesObject = {
          name: metricDesc,
          type: 'line',
          smooth: true,
          symbol: datapoints.length > 5 && !symbol ? 'none' : 'emptyCircle',
          data: datapoints.map(({ value }) => value),
          showSymbol: false,
          lineStyle: {
            width: 2
          }
        }
        // 取最大值以及最大单位
        const max = Math.max.apply(null, seriesObject.data)
        maxValue = max > maxValue ? max : maxValue
        const min = Math.min.apply(null, seriesObject.data)
        minValue = min > minValue ? min : minValue
        series.push(seriesObject)
      })
      setChartData({
        xData,
        series,
        maxValue,
        minValue
      })
    } else {
      setChartData(undefined)
    }
  }

  const openChartDiabol = () => {
    openDetails({
      instanceId,
      graphId: graph.graphId,
      units: graph.units,
      title: graph.title,
      aggregate
    })
  }

  const { width: winWidth, height: winHeight } = useWindowSize()
  useDebounce(
    () => {
      if (isRequest) {
        chartRef?.current?.chartResize()
      }
    },
    100,
    [isRequest, width, height, winWidth, winHeight]
  )

  return (
    <div className={className} id={`chart-item-${graph.graphId}`}>
      <div className="chart-header">
        <div className="chart-header-title">
          <p>
            {_$(graph.title)}
            {graph.units ? <span style={{ color: '#999' }}>({graph.units})</span> : ''}
          </p>
        </div>
        <div className="chart-header-active">
          <Select
            width={80}
            size="small"
            value={aggregate}
            onChangeValue={value => {
              setAggregate(value)
              getChartList({ Aggregate: value })
            }}
          >
            {AggregatorEnum.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
          <div className="chart-header-icon">
            <Icon className="ion-arrow-expand" size={22} onClick={openChartDiabol} />
          </div>
        </div>
      </div>
      <div className="chart-box">
        {chartData ? (
          <MonitorChartItemApp
            ref={chartRef}
            xData={chartData.xData}
            series={chartData.series}
            title={graph.title}
            maxValue={chartData.maxValue}
            minValue={chartData.minValue}
            units={graph?.units}
            interval={interval}
          />
        ) : (
          <NoDataComponent />
        )}
      </div>
    </div>
  )
}

export default React.memo(styled(MonitorChartItem)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all ease-in-out 0.2s;
  border: 1px solid #eee;
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px 0px 24px;
    .chart-header-title {
      p {
        font-size: 14px;
        color: #262626;
        line-height: 22px;
        transition: all ease-in-out 0.2s;
        margin-bottom: 0;
        vertical-align: bottom;
        span {
          font-size: 12px;
          margin-left: 5px;
        }
      }
    }
    .chart-header-active {
      display: flex;
      align-items: center;
      & > * {
        margin-left: 8px;
      }
      .chart-header-icon {
        cursor: pointer;
        color: #262626;
        transition: all ease-in-out 0.2s;
        &:hover {
          color: #0091ea;
        }
      }
    }
  }
  .chart-box {
    width: 100%;
    height: calc(100% - 49px);
    padding-bottom: 5px;
  }
  &:hover {
    box-shadow: 0 3px 10px 2px rgba(0, 0, 0, 0.16);
    .chart-header-title p {
      color: #0091ea;
    }
  }
`)
